import { Loader, Text } from "@mantine/core";
import {
  IconDownload,
  IconFileSpreadsheet,
  IconFileTypeCsv,
  IconFileTypePdf,
} from "@tabler/icons-react";
import type { IconProps } from "@tabler/icons-react";
import type { ComponentType } from "react";
import { useState } from "react";

import api from "#/api/client";
import { assistantApi } from "#/api/assistantApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { AssistantExportFormat } from "#/types/assistant";

import { GlowIconButton } from "./GlowIconButton";

interface ExportSpec {
  title?: string;
  filename?: string;
  format: AssistantExportFormat;
  sql: string;
}

const FORMAT_META: Record<
  AssistantExportFormat,
  { icon: ComponentType<IconProps>; color: string }
> = {
  [AssistantExportFormat.XLSX]: { icon: IconFileSpreadsheet, color: "#059669" },
  [AssistantExportFormat.CSV]: { icon: IconFileTypeCsv, color: "#2563EB" },
  [AssistantExportFormat.PDF]: { icon: IconFileTypePdf, color: "#DC2626" },
};

/** Renders a downloadable file card from a `tma-export` fenced block. */
export const AssistantExportCard = ({ raw }: { raw: string }) => {
  const [downloading, setDownloading] = useState(false);
  const spec = parseSpec(raw);

  if (!spec) {
    return (
      <Text size="xs" c="dimmed" className="italic">
        (export unavailable)
      </Text>
    );
  }

  const meta = FORMAT_META[spec.format];
  const Icon = meta.icon;
  const displayName = `${spec.filename ?? "export"}.${spec.format}`;

  const handleDownload = async () => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    try {
      const { data } = await assistantApi.exportData({
        sql: spec.sql,
        format: spec.format,
        filename: spec.filename,
        title: spec.title,
      });
      const binary = atob(data.base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      api.downloadFile(bytes, data.filename, data.mimeType);
      showSuccessNotification({ message: "File downloaded." });
    } catch {
      showErrorNotification({ message: "Failed to generate the file." });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="my-2 flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${meta.color}1a` }}
      >
        <Icon size={24} color={meta.color} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-800">
          {displayName}
        </div>
        <div className="text-xs text-slate-400">
          {spec.format.toUpperCase()} file · click to download
        </div>
      </div>
      {downloading ? (
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center">
          <Loader size="sm" color="grape" />
        </div>
      ) : (
        <GlowIconButton
          icon={IconDownload}
          variant="solid"
          onClick={() => void handleDownload()}
          ariaLabel="Download file"
        />
      )}
    </div>
  );
};

const parseSpec = (raw: string): ExportSpec | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const spec = parsed as Record<string, unknown>;
  const format = spec.format;
  if (
    format !== AssistantExportFormat.XLSX &&
    format !== AssistantExportFormat.CSV &&
    format !== AssistantExportFormat.PDF
  ) {
    return null;
  }
  if (typeof spec.sql !== "string" || spec.sql.trim().length === 0) {
    return null;
  }
  return {
    format,
    sql: spec.sql,
    title: typeof spec.title === "string" ? spec.title : undefined,
    filename: typeof spec.filename === "string" ? spec.filename : undefined,
  };
};
